/** SUNTRONIX ONE — Mapping Review: resolve customer & service mapping
 *  flags left by the Vtiger historical quote import. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { GitMerge, RefreshCw, Search, Plus, Check } from "lucide-react";

const fmtINR = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);

function CustomerRow({ q, onResolved }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);

  const search = async (t) => {
    setTerm(t);
    if (t.trim().length < 2) return setHits([]);
    const { data } = await api.get("/mapping-review/customer-search", { params: { q: t } });
    setHits(data.items || []);
  };

  const resolve = async (cust) => {
    setBusy(true);
    try {
      await api.post(`/mapping-review/customers/${q.id}/resolve`, { customer_account_id: cust.id });
      toast.success(`${q.code} linked to ${cust.account_name}`);
      onResolved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resolve failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-3" data-testid={`customer-review-${q.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-mono font-semibold text-slate-800">{q.code}</span>
        <span className="text-xs text-slate-500">{q.quote_date?.slice(0, 10) || "—"}</span>
        <span className="text-xs text-slate-600 flex-1 truncate">{q.subject || "(no subject)"}</span>
        <span className="text-xs font-mono">{fmtINR(q.grand_total)}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">NO CUSTOMER AT SOURCE</span>
        <button onClick={() => setOpen(!open)} data-testid={`resolve-customer-btn-${q.id}`}
          className="h-8 px-3 text-xs bg-emerald-700 text-white rounded">{open ? "Close" : "Assign customer"}</button>
      </div>
      {open && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <div className="relative w-96">
            <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
            <input autoFocus value={term} onChange={(e) => search(e.target.value)}
              placeholder="Search Customer Master…" data-testid={`customer-search-${q.id}`}
              className="h-8 pl-7 pr-2 border border-slate-300 rounded text-sm w-full" />
          </div>
          <div className="mt-2 space-y-1 max-h-52 overflow-auto">
            {hits.map((c) => (
              <button key={c.id} disabled={busy} onClick={() => resolve(c)}
                data-testid={`pick-customer-${c.id}`}
                className="w-full text-left px-2.5 py-1.5 text-xs rounded border border-slate-200 hover:bg-emerald-50 flex items-center gap-2 disabled:opacity-50">
                <span className="font-medium text-slate-800 flex-1">{c.account_name}</span>
                <span className="text-slate-400 font-mono">{c.customer_code || ""}</span>
                <Check className="h-3.5 w-3.5 text-emerald-600" />
              </button>))}
            {term.length >= 2 && hits.length === 0 && <div className="text-xs text-slate-400 px-2 py-1">No matches.</div>}
          </div>
        </div>)}
    </div>
  );
}

function ServiceRow({ row, services, categories, onResolved }) {
  const [sid, setSid] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ service_name: row.name, category: "Other", gst_percent: 18 });
  const [busy, setBusy] = useState(false);

  const resolve = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post("/mapping-review/services/resolve", { name: row.name, ...payload });
      toast.success(`"${row.name}" mapped — ${data.quotes_updated} quotes updated`);
      onResolved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resolve failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-3" data-testid={`service-review-${row.name.replace(/\W+/g, "-")}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-semibold text-slate-800 flex-1">{row.name}</span>
        <span className="text-[11px] text-slate-500">{row.lines} lines · {row.quote_count} quotes · {fmtINR(row.total_value)}</span>
        <select value={sid} onChange={(e) => setSid(e.target.value)}
          data-testid={`service-select-${row.name.replace(/\W+/g, "-")}`}
          className="h-8 px-1.5 text-xs border border-slate-300 rounded w-64">
          <option value="">Map to existing service…</option>
          {services.map((s) => <option key={s.id} value={s.id}>{s.service_name}{s.service_code ? ` (${s.service_code})` : ""}</option>)}
        </select>
        <button disabled={!sid || busy} onClick={() => resolve({ service_id: sid })}
          data-testid={`map-service-btn-${row.name.replace(/\W+/g, "-")}`}
          className="h-8 px-3 text-xs bg-emerald-700 text-white rounded disabled:opacity-40">Map</button>
        <button onClick={() => setCreating(!creating)}
          data-testid={`create-service-toggle-${row.name.replace(/\W+/g, "-")}`}
          className="h-8 px-3 text-xs border border-violet-300 text-violet-700 rounded inline-flex items-center gap-1">
          <Plus className="h-3 w-3" /> New service</button>
      </div>
      {creating && (
        <div className="mt-3 border-t border-slate-100 pt-3 flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-[10px] text-slate-500 block">Service name</label>
            <input value={form.service_name} onChange={(e) => setForm({ ...form, service_name: e.target.value })}
              className="h-8 px-2 border border-slate-300 rounded text-xs w-72" data-testid="new-service-name" />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 block">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="h-8 px-1.5 border border-slate-300 rounded text-xs" data-testid="new-service-category">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 block">GST %</label>
            <input type="number" value={form.gst_percent}
              onChange={(e) => setForm({ ...form, gst_percent: Number(e.target.value) })}
              className="h-8 px-2 border border-slate-300 rounded text-xs w-20" />
          </div>
          <button disabled={busy} onClick={() => resolve({ create: form })}
            data-testid="create-and-map-btn"
            className="h-8 px-3 text-xs bg-violet-700 text-white rounded disabled:opacity-50">Create & map</button>
        </div>)}
    </div>
  );
}

export default function MappingReview() {
  const [tab, setTab] = useState("services");
  const [summary, setSummary] = useState({});
  const [customers, setCustomers] = useState([]);
  const [svcData, setSvcData] = useState({ items: [], services: [], categories: [] });

  const load = useCallback(() => Promise.all([
    api.get("/mapping-review/summary").then(({ data }) => setSummary(data)),
    api.get("/mapping-review/customers").then(({ data }) => setCustomers(data.items || [])),
    api.get("/mapping-review/services").then(({ data }) => setSvcData(data)),
  ]).catch(() => toast.error("Failed to load mapping review data")), []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-[1300px] mx-auto space-y-4" data-testid="mapping-review-page">
      <header className="flex items-center gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-lg bg-emerald-900 text-white grid place-items-center"><GitMerge className="h-5 w-5" /></div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">Mapping Review</h1>
          <p className="text-xs text-slate-500">Resolve customer & service mappings flagged during the legacy historical import</p>
        </div>
        <button onClick={load} className="h-9 px-3 text-sm border border-slate-300 rounded inline-flex items-center gap-1.5" data-testid="mapping-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
      </header>

      <div className="flex gap-1.5">
        <button onClick={() => setTab("services")} data-testid="tab-services"
          className={`h-8 px-3 text-xs font-medium rounded-full border ${tab === "services" ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-slate-600 border-slate-300"}`}>
          Service mappings ({svcData.items.length})</button>
        <button onClick={() => setTab("customers")} data-testid="tab-customers"
          className={`h-8 px-3 text-xs font-medium rounded-full border ${tab === "customers" ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-slate-600 border-slate-300"}`}>
          Customer mappings ({customers.length})</button>
      </div>

      {tab === "customers" && (
        <div className="space-y-2">
          {customers.length === 0 && <div className="p-8 text-center text-slate-400 bg-white border border-slate-200 rounded-md">All customer mappings resolved 🎉</div>}
          {customers.map((q) => <CustomerRow key={q.id} q={q} onResolved={load} />)}
        </div>)}

      {tab === "services" && (
        <div className="space-y-2">
          {svcData.items.length === 0 && <div className="p-8 text-center text-slate-400 bg-white border border-slate-200 rounded-md">All service mappings resolved 🎉</div>}
          {svcData.items.map((r) => (
            <ServiceRow key={r.name} row={r} services={svcData.services}
              categories={svcData.categories} onResolved={load} />))}
        </div>)}
    </div>
  );
}
